import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import Refresh from '@mui/icons-material/Refresh';
import Visibility from '@mui/icons-material/Visibility';
import Close from '@mui/icons-material/Close';
import Search from '@mui/icons-material/Search';
import Edit from '@mui/icons-material/Edit';
import DeleteOutline from '@mui/icons-material/DeleteOutline';
import PostAdd from '@mui/icons-material/PostAdd';
import UploadFile from '@mui/icons-material/UploadFile';
import OpenInNew from '@mui/icons-material/OpenInNew';
import PageLayout from '../components/common/PageLayout';
import ProviderLogo from '../components/recon/ProviderLogo';
import ZelleReconciliationDialog from '../components/orders/ZelleReconciliationDialog';
import { get, post, patch, put, del } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import {
  buildZelleOrdersSnapshot,
  saveZelleOrdersSnapshotToSession,
  readZelleInitialFilterState,
} from '../utils/zelleOrdersPersist';

function formatDate(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch {
    return String(iso);
  }
}

function formatAmount(cents) {
  if (cents == null || cents === '') return '—';
  const n = Number(cents) / 100;
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(n);
}

function isOrderNotFoundInWc(err) {
  const d = err?.data || {};
  const msg = String(err?.message || '');
  return err?.status === 404 && (d?.code === 'ORDER_NOT_FOUND_IN_WC' || /not exist in WooCommerce|Order not found in WooCommerce/i.test(msg));
}

// The three deterministic ways a Zelle payment links to an order (plain language for the Match column).
const MATCH_LABEL = {
  zelle_message_id: 'Zelle email ↔ order',
  woo_order_id: 'Woo order id',
  memo_code: 'Memo code',
};

// Resolve the order for a row: the server-resolved order (any of the 3 linkages), else a bare legacy
// woo_order_id that didn't resolve locally (still counts as "has an order", flagged in comments).
function orderOf(row) {
  if (row?.resolved_order) return row.resolved_order;
  const legacy = row?.wooOrderId ?? row?.woo_order_id;
  if (legacy != null && legacy !== '' && Number(legacy) > 0) {
    return { id: Number(legacy), label: `#${legacy}`, order_number: null, woo_order_id: Number(legacy), resolved_via: 'woo_order_id' };
  }
  return null;
}

const SOURCE_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'kiosk', label: 'Kiosk' },
  { value: 'non_kiosk', label: 'Non-kiosk' },
];

const STATUS_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'pending', label: 'Pending' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'failed', label: 'Failed' },
];

// Order / No order / All — mirrors Stripe Reconciliation. Drives the server-side linkage filter.
const STATUS_TABS = [
  { key: 'no_order', label: 'No order' },
  { key: 'order', label: 'Order' },
  { key: 'all', label: 'All' },
];

const fmtMoney = (n) => `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Last 12 months + "All" (all pulled Zelle payments), for the header month selector.
function buildMonthOptions() {
  const opts = [];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    opts.push({ value: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`, label: d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }) });
  }
  opts.push({ value: 'all', label: 'All payments' });
  return opts;
}

// A 'YYYY-MM' month → inclusive {from, to} YYYY-MM-DD range (last day via day-0 of next month). 'all' clears.
function monthToRange(month) {
  if (!month || month === 'all') return { from: '', to: '' };
  const [y, m] = month.split('-').map(Number);
  const last = new Date(y, m, 0).getDate();
  return { from: `${month}-01`, to: `${month}-${String(last).padStart(2, '0')}` };
}

export default function ZelleReconciliationPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { staff } = useAuth();
  const [orders, setOrders] = useState([]);
  const [total, setTotal] = useState(0);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sessionExpired, setSessionExpired] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [creatingId, setCreatingId] = useState(null);
  const [createdOrder, setCreatedOrder] = useState(null); // { orderId } — success dialog after Create order
  const [viewRow, setViewRow] = useState(null);
  const [editRow, setEditRow] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [editForm, setEditForm] = useState({ status: '', woo_order_id: '' });
  const [editError, setEditError] = useState('');
  const [editSuccess, setEditSuccess] = useState('');
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [duplicateRows, setDuplicateRows] = useState([]);
  const [duplicateLoading, setDuplicateLoading] = useState(false);
  const [duplicateError, setDuplicateError] = useState('');
  const [duplicateDeletingOrderId, setDuplicateDeletingOrderId] = useState(null);
  const [duplicateDeleteCandidate, setDuplicateDeleteCandidate] = useState(null);
  const [bankReconOpen, setBankReconOpen] = useState(false); // upload-bank-statement dialog

  const perms = Array.isArray(staff?.permissions) ? staff.permissions : [];
  const isAdminPermission = perms.includes('admin:full') || String(staff?.role || '').toLowerCase() === 'admin';
  const canDeleteOrders = isAdminPermission || perms.includes('orders:delete');
  const canCreateOrders = isAdminPermission || perms.includes('orders:create');

  // Filters (initial values restored from session when returning from /orders/:id/edit)
  const zelleFilterInitRef = React.useRef(null);
  if (zelleFilterInitRef.current === null) {
    zelleFilterInitRef.current = readZelleInitialFilterState();
  }
  const zf0 = zelleFilterInitRef.current;

  const [source, setSource] = useState(zf0.source);
  const [statusFilter, setStatusFilter] = useState(zf0.statusFilter);
  const [memoSearch, setMemoSearch] = useState(zf0.memoSearch);
  const [orderIdSearch, setOrderIdSearch] = useState(zf0.orderIdSearch);
  const [dateFromFilter, setDateFromFilter] = useState(zf0.dateFromFilter);
  const [dateToFilter, setDateToFilter] = useState(zf0.dateToFilter);
  const [searchText, setSearchText] = useState(zf0.searchText);
  const [page, setPage] = useState(zf0.page);
  // Order / No order / All tab → server linkage filter (missing_order / has_order).
  const [tab, setTab] = useState('all');
  // Month selector (last 12 months + All) drives the date range; stat cards summarize the period.
  const monthOptions = React.useMemo(buildMonthOptions, []);
  const [month, setMonth] = useState(monthOptions[0].value);
  const [summary, setSummary] = useState(null);
  const limit = 50;

  // Month → date range (the From/To fields follow it; "All" clears them).
  useEffect(() => {
    const { from, to } = monthToRange(month);
    setDateFromFilter(from);
    setDateToFilter(to);
    setPage(0);
  }, [month]);

  // Header stat cards: total / has-order / missing (+$), for the active source + status + date range.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const params = {};
        if (source) params.source = source;
        if (statusFilter) params.status = statusFilter;
        if (dateFromFilter) params.date_from = dateFromFilter;
        if (dateToFilter) params.date_to = dateToFilter;
        const s = await get('/api/zelle/admin/summary', params);
        if (!cancelled) setSummary(s);
      } catch { if (!cancelled) setSummary(null); }
    })();
    return () => { cancelled = true; };
  }, [source, statusFilter, dateFromFilter, dateToFilter]);

  const memoSearchDebounceRef = React.useRef(null);
  useEffect(() => {
    if (memoSearchDebounceRef.current) clearTimeout(memoSearchDebounceRef.current);
    memoSearchDebounceRef.current = setTimeout(() => setMemoSearch(searchText.trim()), 300);
    return () => { if (memoSearchDebounceRef.current) clearTimeout(memoSearchDebounceRef.current); };
  }, [searchText]);

  const loadOrders = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = { limit, offset: page * limit };
      if (source) params.source = source;
      if (statusFilter) params.status = statusFilter;
      if (memoSearch.trim()) params.search = memoSearch.trim();
      if (orderIdSearch.trim()) params.woo_order_id = orderIdSearch.trim();
      if (dateFromFilter) params.date_from = dateFromFilter;
      if (dateToFilter) params.date_to = dateToFilter;
      if (tab === 'no_order') params.missing_order = 1;
      if (tab === 'order') params.has_order = 1;
      const data = await get('/api/zelle/admin/orders', params);
      const list = Array.isArray(data?.orders) ? data.orders : (Array.isArray(data?.data) ? data.data : []);
      setOrders(list);
      const rawTotal =
        data?.total ??
        data?.meta?.total ??
        data?.count ??
        data?.total_count ??
        data?.pagination?.total;
      const parsedTotal = Number(rawTotal);
      const normalizedTotal = Number.isFinite(parsedTotal) && parsedTotal >= 0 ? parsedTotal : null;
      setTotal(normalizedTotal ?? list.length);
      const nextFlag =
        Boolean(data?.next_page_token) ||
        Boolean(data?.nextPageToken) ||
        Boolean(data?.has_more) ||
        Boolean(data?.hasMore);
      const fallbackHasNext = normalizedTotal != null
        ? ((page + 1) * limit < normalizedTotal)
        : (list.length >= limit);
      setHasNextPage(nextFlag || fallbackHasNext);
      saveZelleOrdersSnapshotToSession(buildZelleOrdersSnapshot({
        source,
        statusFilter,
        searchText,
        memoSearch,
        orderIdSearch,
        dateFromFilter,
        dateToFilter,
        page,
      }));
    } catch (e) {
      const msg = e?.message || 'Failed to load Zelle orders';
      const isAuthError = e?.status === 401 || /unauthorized|session expired|session idle/i.test(String(msg));
      setSessionExpired(!!isAuthError);
      setError(isAuthError ? 'Session expired. Please log in again.' : msg);
      setOrders([]);
      setTotal(0);
      setHasNextPage(false);
    } finally {
      setLoading(false);
    }
  }, [page, source, statusFilter, memoSearch, orderIdSearch, dateFromFilter, dateToFilter, tab]);

  const loadDuplicateOrders = useCallback(async () => {
    setDuplicateLoading(true);
    setDuplicateError('');
    try {
      const data = await get('/api/zelle/admin/duplicate-orders');
      const list = Array.isArray(data?.duplicates) ? data.duplicates : [];
      setDuplicateRows(list);
    } catch (e) {
      setDuplicateError(e?.message || 'Failed to load duplicate orders');
      setDuplicateRows([]);
    } finally {
      setDuplicateLoading(false);
    }
  }, []);

  const persistZelleFiltersBeforeLeave = useCallback(() => {
    saveZelleOrdersSnapshotToSession(buildZelleOrdersSnapshot({
      source,
      statusFilter,
      searchText,
      memoSearch,
      orderIdSearch,
      dateFromFilter,
      dateToFilter,
      page,
    }));
  }, [source, statusFilter, searchText, memoSearch, orderIdSearch, dateFromFilter, dateToFilter, page]);

  const openWooOrderEdit = useCallback((rawId) => {
    const id = Number(rawId);
    if (!Number.isFinite(id) || id <= 0) return;
    persistZelleFiltersBeforeLeave();
    navigate(`/orders/${id}/edit`, { state: { returnTo: `${location.pathname}${location.search}` } });
  }, [navigate, location.pathname, location.search, persistZelleFiltersBeforeLeave]);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  useEffect(() => {
    if (!location.state?.orderEditSaved) return;
    loadOrders();
    navigate(`${location.pathname}${location.search}`, { replace: true, state: {} });
  }, [location.state?.orderEditSaved, loadOrders, navigate, location.pathname, location.search]);

  useEffect(() => {
    if (editRow) {
      setEditError('');
      setEditSuccess('');
      setEditForm({
        status: editRow.status ?? '',
        woo_order_id: (editRow.wooOrderId ?? editRow.woo_order_id) != null ? String(editRow.wooOrderId ?? editRow.woo_order_id) : '',
      });
    }
  }, [editRow]);

  const handleOpenDuplicateDialog = async () => {
    setDuplicateDialogOpen(true);
    await loadDuplicateOrders();
  };

  const handleCloseDuplicateDialog = () => {
    if (duplicateDeletingOrderId) return;
    setDuplicateDialogOpen(false);
    setDuplicateError('');
  };

  const openView = (row) => setViewRow(row);
  const closeView = () => setViewRow(null);

  const openEditDialog = (row) => {
    setEditError('');
    setEditSuccess('');
    setEditRow(row);
  };
  const closeEditDialog = () => {
    if (!editLoading) {
      setEditError('');
      setEditSuccess('');
      setEditRow(null);
    }
  };

  const handleEditSubmit = async () => {
    if (!editRow) return;
    setEditLoading(true);
    setEditError('');
    setEditSuccess('');
    try {
      const updated = await patch(`/api/zelle/admin/orders/${editRow.id}`, {
        status: editForm.status.trim() || undefined,
        woo_order_id: editForm.woo_order_id.trim() ? editForm.woo_order_id.trim() : undefined,
      });
      setEditRow((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          ...(updated || {}),
          status: updated?.status ?? editForm.status,
          woo_order_id: updated?.woo_order_id ?? (editForm.woo_order_id.trim() || null),
        };
      });
      setEditSuccess('Zelle payment updated successfully.');
      await loadOrders();
      closeView();
    } catch (e) {
      setEditError(e?.message || 'Update failed');
    } finally {
      setEditLoading(false);
    }
  };

  const hasWooOrder = (row) => {
    const id = row.wooOrderId ?? row.woo_order_id;
    return id != null && id !== '';
  };
  const isPendingOrCancelled = (row) => {
    const s = String(row?.status || '').toLowerCase();
    return s === 'pending' || s === 'cancelled' || s === 'canceled';
  };
  const canDeleteRow = (row) => {
    const sourceValue = String(row?.source || '').toLowerCase();
    return sourceValue === 'kiosk' && isPendingOrCancelled(row);
  };
  // Manual create-order: only for a payment with NO order under any linkage (resolved_order null) AND
  // settled (completed) — pending/failed are never creatable. Books via the unified queue path.
  const canCreateOrderRow = (row) => canCreateOrders && !row.resolved_order && !hasWooOrder(row)
    && String(row?.status || '').toLowerCase() === 'completed';
  const handleCreateOrderRow = async (row) => {
    if (!canCreateOrderRow(row)) return;
    const amt = formatAmount(row.amount_cents ?? row.amountCents);
    const who = row.sent_by || row.sentBy || row.customer_name || 'this donor';
    const ok = window.confirm(
      `Create a donation order for ${amt} from ${who}?\n\n`
      + '• Product: General Donation\n'
      + '• Backdated to the payment date\n'
      + '• No receipt email is sent to the donor\n\n'
      + 'This books a REAL donation in the ledger.'
    );
    if (!ok) return;
    setCreatingId(row.id);
    try {
      const resp = await post(`/api/zelle/admin/orders/${row.id}/create-order`, {});
      await loadOrders();
      if (viewRow?.id === row.id) setViewRow(null);
      setCreatedOrder({ orderId: resp?.order_id ?? null });
    } catch (e) {
      alert(e?.message || 'Create order failed');
    } finally {
      setCreatingId(null);
    }
  };
  const handleDeleteRow = async (row) => {
    if (!canDeleteRow(row)) return;
    const ok = window.confirm(`Delete Zelle row #${row.id}? This cannot be undone.`);
    if (!ok) return;
    setDeletingId(row.id);
    try {
      await del(`/api/zelle/admin/orders/${row.id}`);
      await loadOrders();
      if (viewRow?.id === row.id) setViewRow(null);
    } catch (e) {
      alert(e?.message || 'Delete failed');
    } finally {
      setDeletingId(null);
    }
  };
  const handleDeleteDuplicateOrder = async (row) => {
    setDuplicateDeleteCandidate(row);
  };

  const handleCloseDuplicateDeleteConfirm = () => {
    if (duplicateDeletingOrderId) return;
    setDuplicateDeleteCandidate(null);
  };

  const handleConfirmDeleteDuplicateOrder = async () => {
    const row = duplicateDeleteCandidate;
    const duplicateOrderId = Number(row?.duplicate_order_id);
    if (!Number.isFinite(duplicateOrderId) || duplicateOrderId <= 0) return;

    setDuplicateDeletingOrderId(duplicateOrderId);
    try {
      await put(`/api/orders/${duplicateOrderId}/cancel`, {
        suppress_email: true,
        internal_note: `Deleted by ${staff?.name || staff?.email || 'staff user'} due to duplicate order. Original order #${row.original_order_id}.`,
      });
      await loadDuplicateOrders();
      await loadOrders();
      setDuplicateDeleteCandidate(null);
    } catch (e) {
      try {
        if (isOrderNotFoundInWc(e)) {
          await del(`/api/orders/${duplicateOrderId}/local`);
          await loadDuplicateOrders();
          await loadOrders();
          setDuplicateDeleteCandidate(null);
          return;
        }
      } catch (localErr) {
        alert(localErr?.message || 'Failed to delete duplicate order locally');
        return;
      }
      alert(e?.message || 'Failed to trash duplicate order');
    } finally {
      setDuplicateDeletingOrderId(null);
    }
  };

  const renderOrderCell = (row) => {
    const ord = orderOf(row);
    if (!ord) {
      return <Chip size="small" color="error" variant="filled" label="No order" />;
    }
    const label = ord.order_number || ord.label || `#${ord.woo_order_id || ord.id}`;
    return (
      <Stack direction="row" alignItems="center" spacing={0.5} flexWrap="wrap" useFlexGap>
        <Chip
          size="small"
          color="success"
          variant="outlined"
          label={`Order ${label}`.trim()}
          onClick={() => openView(row)}
          sx={{ cursor: 'pointer' }}
        />
        {(row.comments || '').trim() && (
          <Tooltip title={row.comments}>
            <Chip size="small" color="warning" variant="outlined" label="check" />
          </Tooltip>
        )}
      </Stack>
    );
  };

  return (
    <PageLayout contentMaxWidth="xl" showHeaderImage={false} showOrgHeader>
      <Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
            <ProviderLogo provider="zelle" />
            <Typography variant="h5" sx={{ fontWeight: 700 }}>Reconciliation</Typography>
          </Box>
          <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
            <FormControl size="small">
              <Select value={month} onChange={(e) => setMonth(e.target.value)} sx={{ minWidth: 160 }}>
                {monthOptions.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
              </Select>
            </FormControl>
            <Button variant="outlined" startIcon={<Search />} onClick={handleOpenDuplicateDialog}>
              Duplicate Orders
            </Button>
            <Button variant="contained" startIcon={<UploadFile />} onClick={() => setBankReconOpen(true)}>
              Bank Statement Reconciliation
            </Button>
          </Stack>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Every Zelle payment (from <code>kiosk_zelle_orders</code>) cross-checked against the order ledger —
          whether it has an order, and how it was matched. Book a missing order inline, or use
          <strong> Bank Statement Reconciliation</strong> to upload a bank statement and create missing orders in bulk.
        </Typography>

        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>Filters &amp; search</Typography>
          <Stack spacing={2}>
            <Stack direction="row" flexWrap="wrap" columnGap={2} rowGap={1.5} alignItems="center" useFlexGap>
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>Source</InputLabel>
                <Select
                  value={source}
                  label="Source"
                  displayEmpty
                  renderValue={(value) => {
                    const selected = SOURCE_OPTIONS.find((o) => o.value === value);
                    return selected ? selected.label : 'All';
                  }}
                  onChange={(e) => { setSource(e.target.value); setPage(0); }}
                >
                  {SOURCE_OPTIONS.map((o) => (
                    <MenuItem key={o.value || 'all'} value={o.value}>{o.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  displayEmpty
                  renderValue={(value) => {
                    const selected = STATUS_OPTIONS.find((o) => o.value === value);
                    return selected ? selected.label : 'All';
                  }}
                  onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
                >
                  {STATUS_OPTIONS.map((o) => (
                    <MenuItem key={o.value || 'all'} value={o.value}>{o.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                size="small"
                label="Sent by / Memo"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                placeholder="Search sent-by or memo text"
                sx={{ minWidth: 200, flex: '1 1 220px', maxWidth: 420 }}
              />
              <TextField
                size="small"
                label="Order ID"
                value={orderIdSearch}
                onChange={(e) => setOrderIdSearch(e.target.value)}
                placeholder="WooCommerce order ID"
                sx={{ width: 140 }}
              />
              <Button variant="outlined" startIcon={<Search />} onClick={() => setPage(0)}>
                Apply
              </Button>
            </Stack>
            <Stack direction="row" flexWrap="wrap" columnGap={2} rowGap={1.5} alignItems="center" useFlexGap>
              <TextField
                size="small"
                label="From"
                type="date"
                value={dateFromFilter}
                onChange={(e) => { setDateFromFilter(e.target.value); setPage(0); }}
                InputLabelProps={{ shrink: true }}
                sx={{ width: 150 }}
              />
              <TextField
                size="small"
                label="To"
                type="date"
                value={dateToFilter}
                onChange={(e) => { setDateToFilter(e.target.value); setPage(0); }}
                InputLabelProps={{ shrink: true }}
                sx={{ width: 150 }}
              />
            </Stack>
          </Stack>
        </Paper>

        {error && (
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
            <Typography color="error" variant="body2">{error}</Typography>
            {sessionExpired && (
              <Button size="small" variant="contained" onClick={() => navigate('/login')}>
                Log in again
              </Button>
            )}
          </Stack>
        )}

        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={6} md={4}>
            <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>Zelle payments</Typography>
              <Typography variant="h4" sx={{ fontWeight: 700, mt: 0.5 }}>{summary ? summary.total : '—'}</Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>in this period</Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} md={4}>
            <Paper variant="outlined" onClick={() => { setTab('order'); setPage(0); }}
              sx={{ p: 2, height: '100%', cursor: 'pointer', borderColor: 'success.light', borderTop: 3, borderTopColor: 'success.main' }}>
              <Typography variant="caption" sx={{ color: 'success.dark', fontWeight: 600 }}>Order</Typography>
              <Typography variant="h4" sx={{ fontWeight: 700, mt: 0.5, color: 'success.dark' }}>{summary ? summary.with_order : '—'}</Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>has an order</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={4}>
            <Paper variant="outlined" onClick={() => { setTab('no_order'); setPage(0); }}
              sx={{ p: 2, height: '100%', cursor: 'pointer', borderColor: 'error.light', borderTop: 3, borderTopColor: 'error.main' }}>
              <Typography variant="caption" sx={{ color: 'error.dark', fontWeight: 600 }}>No order · at risk</Typography>
              <Typography variant="h4" sx={{ fontWeight: 700, mt: 0.5, color: 'error.dark' }}>{summary ? summary.missing : '—'}</Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>{summary ? fmtMoney(summary.missing_amount) : 'needs an order booked'}</Typography>
            </Paper>
          </Grid>
        </Grid>

        <Paper variant="outlined">
          <Tabs value={tab} onChange={(e, v) => { setTab(v); setPage(0); }} sx={{ borderBottom: 1, borderColor: 'divider' }}>
            {STATUS_TABS.map((t) => (
              <Tab key={t.key} value={t.key} label={t.label} />
            ))}
          </Tabs>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ overflow: 'auto' }}>
              <Table size="small" stickyHeader sx={{ minWidth: 920 }}>
                <TableHead>
                  <TableRow sx={{ '& th': { bgcolor: 'grey.100', fontWeight: 600 } }}>
                    <TableCell>Date</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell>Sent by</TableCell>
                    <TableCell>Memo</TableCell>
                    <TableCell>Source</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Order?</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {orders.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ color: 'text.secondary', py: 3 }}>
                        {tab === 'no_order' ? '✓ No missing-order Zelle payments match the filters.' : 'No Zelle payments match the filters.'}
                      </TableCell>
                    </TableRow>
                  ) : (
                    orders.map((row) => (
                      <TableRow key={row.id} hover>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(row.emailDate ?? row.email_date ?? row.createdAt ?? row.created_at)}</TableCell>
                        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{formatAmount(row.amountCents ?? row.amount_cents)}</TableCell>
                        <TableCell sx={{ maxWidth: 200, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={(row.sentBy ?? row.sent_by ?? '').toString()}>{(row.sentBy ?? row.sent_by ?? '—').toString().slice(0, 30)}</TableCell>
                        <TableCell sx={{ maxWidth: 220, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={(row.zelleMemo ?? row.zelle_memo ?? '').toString()}>{(row.zelleMemo ?? row.zelle_memo ?? '—').toString()}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.source ?? '—'}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.status ?? '—'}</TableCell>
                        <TableCell>{renderOrderCell(row)}</TableCell>
                        <TableCell align="right">
                          <Stack direction="row" spacing={0.5} justifyContent="flex-end" flexWrap="wrap">
                            <IconButton size="small" title="View details" onClick={() => openView(row)}>
                              <Visibility fontSize="small" />
                            </IconButton>
                            <IconButton size="small" title="Edit (status & order ID)" onClick={() => openEditDialog(row)}>
                              <Edit fontSize="small" />
                            </IconButton>
                            {canCreateOrderRow(row) && (
                              <IconButton
                                size="small"
                                color="primary"
                                title="Create donation order (General Donation, no receipt)"
                                onClick={() => handleCreateOrderRow(row)}
                                disabled={creatingId === row.id}
                              >
                                {creatingId === row.id ? <CircularProgress size={14} /> : <PostAdd fontSize="small" />}
                              </IconButton>
                            )}
                            {canDeleteRow(row) && (
                              <IconButton
                                size="small"
                                title="Delete"
                                onClick={() => handleDeleteRow(row)}
                                disabled={deletingId === row.id}
                              >
                                {deletingId === row.id ? <CircularProgress size={14} /> : <DeleteOutline fontSize="small" />}
                              </IconButton>
                            )}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
              {(page > 0 || hasNextPage || total > limit) && (
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ p: 1.5 }}>
                  <Typography variant="body2" color="text.secondary">
                    Page {page + 1} (showing up to {limit}{Number.isFinite(total) && total > 0 ? ` of ${total}` : ''})
                  </Typography>
                  <Stack direction="row" spacing={1}>
                    <Button size="small" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>Previous</Button>
                    <Button size="small" disabled={!hasNextPage} onClick={() => setPage((p) => p + 1)}>Next</Button>
                  </Stack>
                </Stack>
              )}
            </Box>
          )}
        </Paper>
      </Box>

      <Dialog open={!!createdOrder} onClose={() => setCreatedOrder(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Order created</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {createdOrder?.orderId
              ? `Order #${createdOrder.orderId} was created and will sync to WooCommerce shortly.`
              : 'The order was queued and will appear shortly.'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Booked as General Donation. Use Edit order to change the product/category or add details.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreatedOrder(null)}>Close</Button>
          <Button
            variant="contained"
            disabled={!createdOrder?.orderId}
            onClick={() => { const id = createdOrder?.orderId; setCreatedOrder(null); if (id) openWooOrderEdit(id); }}
          >
            Edit order
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={duplicateDialogOpen}
        onClose={handleCloseDuplicateDialog}
        maxWidth={false}
        fullWidth
        PaperProps={{
          sx: {
            width: 'min(1440px, calc(100vw - 48px))',
          },
        }}
      >
        <DialogTitle>Duplicate Orders</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Duplicate orders are grouped by the exact shared fields: <strong>`zelle_sent_by`</strong>, <strong>`zelle_email_date`</strong>, and <strong>`total_str`</strong>. Keep the first/original order and review the later duplicate before trashing it.
          </Typography>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
            <Typography variant="body2" color="text.secondary">
              {duplicateRows.length} duplicate order{duplicateRows.length === 1 ? '' : 's'} found
            </Typography>
            <Button size="small" startIcon={duplicateLoading ? <CircularProgress size={14} /> : <Refresh />} onClick={loadDuplicateOrders} disabled={duplicateLoading || !!duplicateDeletingOrderId}>
              Refresh
            </Button>
          </Stack>
          {duplicateError ? <Alert severity="error" sx={{ mb: 2 }}>{duplicateError}</Alert> : null}
          {duplicateLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Paper sx={{ overflow: 'auto' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Sent by</TableCell>
                    <TableCell>Zelle email date</TableCell>
                    <TableCell>Total</TableCell>
                    <TableCell>Original order</TableCell>
                    <TableCell>Original created</TableCell>
                    <TableCell>Duplicate order</TableCell>
                    <TableCell>Duplicate created</TableCell>
                    <TableCell align="right">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {duplicateRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                        No duplicate orders found.
                      </TableCell>
                    </TableRow>
                  ) : duplicateRows.map((row) => {
                    const duplicateOrderId = Number(row.duplicate_order_id);
                    const originalOrderId = Number(row.original_order_id);
                    return (
                      <TableRow key={`duplicate-order-${duplicateOrderId}`}>
                        <TableCell sx={{ minWidth: 200 }}>{row.zelle_sent_by || '—'}</TableCell>
                        <TableCell sx={{ minWidth: 170 }}>{formatDate(row.zelle_email_date)}</TableCell>
                        <TableCell sx={{ minWidth: 90 }}>{row.total_str ? `$${row.total_str}` : '—'}</TableCell>
                        <TableCell>
                          <Stack spacing={0.5}>
                            <Button size="small" onClick={() => openWooOrderEdit(originalOrderId)}>
                              #{originalOrderId}
                            </Button>
                            <Typography variant="caption" color="text.secondary">
                              {row.original_customer_name || '—'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {row.original_item_summary || '—'}
                            </Typography>
                          </Stack>
                        </TableCell>
                        <TableCell sx={{ minWidth: 170 }}>{formatDate(row.original_created_at)}</TableCell>
                        <TableCell>
                          <Stack spacing={0.5}>
                            <Button size="small" color="warning" onClick={() => openWooOrderEdit(duplicateOrderId)}>
                              #{duplicateOrderId}
                            </Button>
                            <Typography variant="caption" color="text.secondary">
                              {row.duplicate_customer_name || '—'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {row.duplicate_item_summary || '—'}
                            </Typography>
                          </Stack>
                        </TableCell>
                        <TableCell sx={{ minWidth: 170 }}>{formatDate(row.duplicate_created_at)}</TableCell>
                        <TableCell align="right">
                          <Button
                            size="small"
                            color="error"
                            variant="outlined"
                            disabled={!canDeleteOrders || duplicateDeletingOrderId === duplicateOrderId}
                            onClick={() => handleDeleteDuplicateOrder(row)}
                          >
                            {duplicateDeletingOrderId === duplicateOrderId ? 'Deleting…' : 'Delete duplicate'}
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Paper>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDuplicateDialog} disabled={!!duplicateDeletingOrderId}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!duplicateDeleteCandidate} onClose={handleCloseDuplicateDeleteConfirm} maxWidth="sm" fullWidth>
        <DialogTitle>Confirm Duplicate Order Delete</DialogTitle>
        <DialogContent>
          {duplicateDeleteCandidate && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Alert severity="warning">
                This will cancel duplicate order #{duplicateDeleteCandidate.duplicate_order_id}. Original order #{duplicateDeleteCandidate.original_order_id} will be kept. A private note will be added so it can be permanently removed later.
              </Alert>
              <Paper variant="outlined" sx={{ p: 1.5 }}>
                <Stack spacing={1}>
                  <Typography variant="body2"><strong>Sent by:</strong> {duplicateDeleteCandidate.zelle_sent_by || '—'}</Typography>
                  <Typography variant="body2"><strong>Zelle email date:</strong> {formatDate(duplicateDeleteCandidate.zelle_email_date)}</Typography>
                  <Typography variant="body2"><strong>Total:</strong> {duplicateDeleteCandidate.total_str ? `$${duplicateDeleteCandidate.total_str}` : '—'}</Typography>
                  <Typography variant="body2"><strong>Original order:</strong> #{duplicateDeleteCandidate.original_order_id} created {formatDate(duplicateDeleteCandidate.original_created_at)}</Typography>
                  <Typography variant="body2"><strong>Original customer:</strong> {duplicateDeleteCandidate.original_customer_name || '—'}</Typography>
                  <Typography variant="body2"><strong>Original items:</strong> {duplicateDeleteCandidate.original_item_summary || '—'}</Typography>
                  <Typography variant="body2"><strong>Duplicate order:</strong> #{duplicateDeleteCandidate.duplicate_order_id} created {formatDate(duplicateDeleteCandidate.duplicate_created_at)}</Typography>
                  <Typography variant="body2"><strong>Duplicate customer:</strong> {duplicateDeleteCandidate.duplicate_customer_name || '—'}</Typography>
                  <Typography variant="body2"><strong>Duplicate items:</strong> {duplicateDeleteCandidate.duplicate_item_summary || '—'}</Typography>
                </Stack>
              </Paper>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDuplicateDeleteConfirm} disabled={!!duplicateDeletingOrderId}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            disabled={!!duplicateDeletingOrderId}
            onClick={handleConfirmDeleteDuplicateOrder}
          >
            {duplicateDeletingOrderId ? 'Cancelling…' : 'Cancel duplicate order'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!editRow} onClose={closeEditDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Zelle payment</DialogTitle>
        <DialogContent>
          {editRow && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              {editError ? <Alert severity="error">{editError}</Alert> : null}
              {editSuccess ? <Alert severity="success">{editSuccess}</Alert> : null}
              <TextField
                size="small"
                label="Date"
                value={formatDate(editRow.emailDate ?? editRow.email_date ?? editRow.createdAt ?? editRow.created_at)}
                InputProps={{ readOnly: true }}
              />
              <TextField
                size="small"
                label="Sent by"
                value={editRow.sentBy ?? editRow.sent_by ?? ''}
                InputProps={{ readOnly: true }}
              />
              <TextField
                size="small"
                label="Amount"
                value={formatAmount(editRow.amountCents ?? editRow.amount_cents)}
                InputProps={{ readOnly: true }}
              />
              <TextField
                size="small"
                label="Memo"
                value={editRow.zelleMemo ?? editRow.zelle_memo ?? ''}
                InputProps={{ readOnly: true }}
                multiline
                minRows={2}
              />
              <TextField
                size="small"
                label="Source"
                value={editRow.source ?? ''}
                InputProps={{ readOnly: true }}
              />
              <FormControl size="small" fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={editForm.status}
                  label="Status"
                  onChange={(e) => setEditForm((f) => ({ ...f, status: e.target.value }))}
                >
                  <MenuItem value="">—</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                  <MenuItem value="cancelled">Cancelled</MenuItem>
                  <MenuItem value="failed">Failed</MenuItem>
                </Select>
              </FormControl>
              <TextField
                size="small"
                label="WC Order ID"
                value={editForm.woo_order_id}
                onChange={(e) => setEditForm((f) => ({ ...f, woo_order_id: e.target.value }))}
                placeholder="Optional"
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEditDialog} disabled={editLoading}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleEditSubmit}
            disabled={editLoading}
            startIcon={editLoading ? <CircularProgress size={18} /> : <Edit />}
          >
            {editLoading ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Drawer anchor="right" open={!!viewRow} onClose={closeView} PaperProps={{ sx: { width: { xs: '100%', sm: 400 } } }}>
        <Box sx={{ p: 2 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
            <Typography variant="h6">Zelle payment details</Typography>
            <IconButton onClick={closeView}><Close /></IconButton>
          </Stack>
          {viewRow && (
            <Stack spacing={1.5}>
              <Row label="ID" value={viewRow.id} />
              <Row label="Gmail message ID" value={viewRow.gmailMessageId ?? viewRow.gmail_message_id} />
              <Row label="Date" value={formatDate(viewRow.emailDate ?? viewRow.email_date ?? viewRow.createdAt ?? viewRow.created_at)} />
              <Row label="Amount" value={formatAmount(viewRow.amountCents ?? viewRow.amount_cents)} />
              <Row label="Sent by" value={viewRow.sentBy ?? viewRow.sent_by} />
              <Row label="Memo" value={viewRow.zelleMemo ?? viewRow.zelle_memo} />
              <Row label="Status" value={viewRow.status} />
              <Row label="Source" value={viewRow.source} />
              {(viewRow.errorMessage ?? viewRow.error_message) && (
                <Row label="Error" value={viewRow.errorMessage ?? viewRow.error_message} />
              )}
              {orderOf(viewRow) ? (
                <Box>
                  <Typography variant="caption" color="text.secondary">Order</Typography>
                  <Typography variant="body2">
                    <Button
                      size="small"
                      startIcon={<OpenInNew fontSize="small" />}
                      onClick={() => {
                        const ord = orderOf(viewRow);
                        const id = ord?.id || ord?.woo_order_id;
                        closeView();
                        if (id) openWooOrderEdit(id);
                      }}
                    >
                      Open order {orderOf(viewRow).order_number || orderOf(viewRow).label}
                    </Button>
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Matched via {MATCH_LABEL[orderOf(viewRow).resolved_via] || '—'}
                  </Typography>
                </Box>
              ) : (
                <Row label="Order" value="No order" />
              )}
            </Stack>
          )}
        </Box>
      </Drawer>

      <ZelleReconciliationDialog
        open={bankReconOpen}
        onClose={() => { setBankReconOpen(false); loadOrders(); }}
      />
    </PageLayout>
  );
}

function Row({ label, value }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>{value ?? '—'}</Typography>
    </Box>
  );
}
